// 档2 — 豆包双向流式 TTS playground（文字→语音，**持续流式**）。与 dialogue 共用云球 UI。
//
// 双向流式 = 一个会话里可**反复发文本**（把 LLM 边生成的文字边送进来），音频边合成边播放。
// 生命周期：连接 → StartConnection → ConnectionStarted(50) → StartSession(100) →
//   SessionStarted(150) → [多次 TaskRequest(200, req_params.text)] → 断开时 FinishSession(102)
//   → SessionFinished(152) → FinishConnection(2)。
// 关键：音频永不中途切断 —— 关 WS 只在收到 SessionFinished（此时上游已发完所有音频）之后；
//   AudioContext 不随会话关闭，留着把已排队的音频放完（仅组件卸载时才关）。

import React from 'react';
import { XPrim, XIcons } from '../../primitives.jsx';
import { XApi } from '../../services.js';
import WsRealtimeOrb from './ws-realtime-orb.jsx';
import { DoubaoRealtimeClient, DoubaoServerEvent as SE } from './ws-doubao-client.js';

const RATE_OUT = 24000; // 下行 PCM Int16 mono

const VOICES = [
  { id: 'zh_female_vv_uranus_bigtts', label: 'Vivi（女）' },
  { id: 'zh_male_liufei_uranus_bigtts', label: '刘飞（男）' },
  { id: 'zh_male_m191_uranus_bigtts', label: '云舟（男）' },
  { id: 'zh_female_cancan_uranus_bigtts', label: '灿灿（女）' },
];
const PHASE_LABEL = { idle: '未连接', connecting: '连接中…', ready: '会话就绪 · 输入文本合成', speaking: '合成播放中…' };

export default function WsPlaygroundDoubaoTts({ api, endpoint }) {
  const wsConfig = (endpoint && endpoint._raw && endpoint._raw.wsConfig) || {};
  const wsBilling = (endpoint && endpoint._raw && endpoint._raw.wsBilling) || {};
  const clientPath = wsConfig.clientPath || '/v1/tts';
  const perCharUsd = Number(wsBilling.perCharUsd) || 0;

  const [status, setStatus] = React.useState('idle'); // idle|connecting|ready|error
  const [text, setText] = React.useState('你好，这是一段豆包语音合成的测试。');
  const [voice, setVoice] = React.useState(VOICES[0].id);
  const [caption, setCaption] = React.useState('');
  const [sentChars, setSentChars] = React.useState(0);
  const [errorMsg, setErrorMsg] = React.useState('');
  const [phase, setPhase] = React.useState('idle');

  const clientRef = React.useRef(new DoubaoRealtimeClient());
  const wsRef = React.useRef(null);
  const statusRef = React.useRef('idle');
  const sidRef = React.useRef(null);
  const chainRef = React.useRef(Promise.resolve());
  const closingRef = React.useRef(false);
  const speakTimerRef = React.useRef(null);
  // 音频（AudioContext 跨会话存活，绝不中途关，避免切断排队音频）
  const playCtxRef = React.useRef(null);
  const playAtRef = React.useRef(0);
  const outAnalyserRef = React.useRef(null);
  const micAnalyserRef = React.useRef(null); // 恒 null（TTS 无麦克风）

  const setStatusBoth = (s) => { statusRef.current = s; setStatus(s); };

  // 仅关 WS（不动 AudioContext，让已排队音频放完）
  const closeWs = React.useCallback(() => {
    if (wsRef.current) { try { wsRef.current.close(1000, 'client stop'); } catch {} wsRef.current = null; }
  }, []);
  // 组件卸载才关音频
  React.useEffect(() => () => {
    closeWs();
    if (playCtxRef.current) { try { playCtxRef.current.close(); } catch {} playCtxRef.current = null; }
    if (speakTimerRef.current) clearTimeout(speakTimerRef.current);
  }, [closeWs]);

  const markSpeaking = React.useCallback(() => {
    setPhase('speaking');
    if (speakTimerRef.current) clearTimeout(speakTimerRef.current);
    // 最后一帧音频排队播完前后回到 ready（云球色）
    const ctx = playCtxRef.current;
    const drainMs = ctx ? Math.max(300, (playAtRef.current - ctx.currentTime) * 1000 + 200) : 500;
    speakTimerRef.current = setTimeout(() => { if (statusRef.current === 'ready') setPhase('ready'); }, drainMs);
  }, []);

  // 播放 Int16 LE 24k（无缝排队；AudioContext 跨会话复用）
  const playAudio = React.useCallback((bytes) => {
    try {
      if (!bytes || bytes.byteLength < 2) return;
      if (!playCtxRef.current) {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        playCtxRef.current = new AudioCtx();
        playAtRef.current = playCtxRef.current.currentTime;
        const an = playCtxRef.current.createAnalyser();
        an.fftSize = 1024; an.connect(playCtxRef.current.destination);
        outAnalyserRef.current = an;
      }
      const ctx = playCtxRef.current;
      if (ctx.state === 'suspended') { try { ctx.resume(); } catch {} }
      const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
      const n = Math.floor(bytes.byteLength / 2);
      if (n === 0) return;
      const buf = ctx.createBuffer(1, n, RATE_OUT);
      const ch = buf.getChannelData(0);
      for (let i = 0; i < n; i++) ch[i] = view.getInt16(i * 2, true) / 0x8000;
      const src = ctx.createBufferSource();
      src.buffer = buf; src.connect(outAnalyserRef.current || ctx.destination);
      const at = Math.max(playAtRef.current, ctx.currentTime);
      src.start(at); playAtRef.current = at + buf.duration;
      markSpeaking();
    } catch { /* 忽略坏帧 */ }
  }, [markSpeaking]);

  const handleFrame = React.useCallback((f) => {
    if (!f) return;
    if (f.msgType === 15) { setErrorMsg(`上游错误 code=${f.code || ''} ${(f.payload && f.payload.error) || ''}`); return; }
    switch (f.event) {
      case SE.ConnectionStarted: // 50 → StartSession（TTS 配置）
        sidRef.current = (window.crypto && crypto.randomUUID) ? crypto.randomUUID() : `s-${Date.now()}`;
        wsRef.current?.send(clientRef.current.startSession(sidRef.current, {
          namespace: 'BidirectionalTTS',
          req_params: { speaker: voice, audio_params: { format: 'pcm', sample_rate: RATE_OUT } },
        }));
        break;
      case SE.SessionStarted: setStatusBoth('ready'); setPhase('ready'); break; // 150 → 可持续发文本
      case SE.TTSResponse: if (f.audio) playAudio(f.audio); break; // 352 音频
      case SE.TTSSentenceEnd: if (f.payload && f.payload.text) setCaption((c) => c + f.payload.text); break; // 351 合成文本
      case SE.SessionFinished: // 152 → 上游已发完所有音频，此时才关 WS（不动音频，放完排队）
        wsRef.current?.send(clientRef.current.finishConnection());
        closeWs(); if (statusRef.current !== 'error') setStatusBoth('idle'); setPhase('ready');
        break;
      case SE.SessionFailed:
      case SE.ConnectionFailed:
        setErrorMsg((f.payload && (f.payload.error || JSON.stringify(f.payload))) || `事件 ${f.event} 失败`);
        break;
      default: break;
    }
  }, [playAudio, closeWs, voice]);

  const connect = async () => {
    if (statusRef.current === 'connecting' || statusRef.current === 'ready') return;
    setErrorMsg(''); setCaption(''); setSentChars(0); closingRef.current = false; sidRef.current = null; // 新会话重置累计
    setStatusBoth('connecting'); setPhase('connecting');
    let token;
    try {
      const minted = await XApi.keyService.getWsToken(endpoint._raw?.id || endpoint.id);
      token = minted && minted.token;
      if (!token) throw new Error('mint returned no token');
    } catch (e) { setStatusBoth('error'); setErrorMsg('ws-token mint 失败: ' + (e && e.message ? e.message : e)); return; }

    const url = XApi.helpers.buildWsGatewayUrl(api.host, clientPath, { endpoint: endpoint._raw?.id || endpoint.id });
    let ws;
    try { ws = new WebSocket(url, XApi.helpers.buildWsCredentialProtocols(token)); } catch (e) { setStatusBoth('error'); setErrorMsg('WebSocket 构造失败: ' + (e && e.message ? e.message : e)); return; }
    ws.binaryType = 'arraybuffer'; wsRef.current = ws;
    ws.onopen = () => ws.send(clientRef.current.startConnection());
    ws.onmessage = (ev) => {
      if (typeof ev.data === 'string') return;
      chainRef.current = chainRef.current.then(async () => { handleFrame(await clientRef.current.parseServerFrame(ev.data)); }).catch(() => {});
    };
    ws.onerror = () => setErrorMsg((m) => m || 'WebSocket error');
    ws.onclose = () => { wsRef.current = null; if (!closingRef.current && statusRef.current !== 'error') setStatusBoth('idle'); if (statusRef.current !== 'ready') setPhase('idle'); };
  };

  // 断开：先 FinishSession（告知无更多文本）→ 等 SessionFinished 收尾关 WS。若上游没回，兜底 3s 强关。
  const disconnect = () => {
    if (statusRef.current !== 'ready') { closeWs(); setStatusBoth('idle'); setPhase('idle'); return; }
    closingRef.current = true;
    try { wsRef.current?.send(clientRef.current.finishSession(sidRef.current)); } catch {}
    setTimeout(() => { if (wsRef.current) { closeWs(); setStatusBoth('idle'); setPhase('idle'); } }, 3000);
  };

  // 发送一段文本进当前会话（可反复；音频流式播放，不结束会话）。
  // ⚠️ 豆包流式 TTS 以句末标点为合成触发；无标点结尾会等最多 4s。discrete「发送合成」= 一句完整
  //    话，故末尾无标点时补「。」→ 首包 ~350ms（否则 ~2.8s）。
  const sendText = () => {
    let t = text.trim();
    if (!t || statusRef.current !== 'ready') return;
    if (!/[。！？…～，、；：.!?,;:]$/.test(t)) t += '。';
    setSentChars((n) => n + [...t].length); // 累计本会话已发字符（= 计费口径）
    try { wsRef.current?.send(clientRef.current.taskRequestText(sidRef.current, t)); } catch {}
    setText(''); // 发送后清空，避免同段被重复计
    setPhase('speaking');
  };

  const connecting = status === 'connecting';
  const ready = status === 'ready';
  const sessionCost = sentChars * perCharUsd; // 本会话累计成本（镜像后端 per-char 结算口径）
  const dot = ready ? 'var(--ok, #35c98a)' : connecting ? 'var(--c-amber, #f0b23a)' : status === 'error' ? 'var(--err)' : 'var(--fg-4)';

  return (
    <div className="col gap-3" style={{ minWidth: 0 }}>
      <div className="card">
        <div className="row between" style={{ padding: '10px 14px', borderBottom: '1px solid var(--line)' }}>
          <div className="micro">语音合成 · 豆包双向流式 TTS（持续流式）</div>
          <div className="row gap-2" style={{ alignItems: 'center', fontSize: 12 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: dot }} />
            <span style={{ color: 'var(--fg-3)' }}>{status}</span>
          </div>
        </div>
        <div style={{ padding: 14 }} className="col gap-3">
          <div className="row gap-3" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
            {!ready && !connecting ? (
              <button className="btn btn-primary" onClick={connect} style={{ height: 38, minWidth: 120 }}><XIcons.IconPlay size={14} /> 连接会话</button>
            ) : (
              <button className="btn" onClick={disconnect} disabled={connecting} style={{ height: 38, minWidth: 120, borderColor: 'var(--err)', color: 'var(--err)' }}>
                {connecting ? <><div className="spinner" style={{ width: 14, height: 14 }} /> 连接中…</> : <><XIcons.IconPause size={14} /> 结束会话</>}
              </button>
            )}
            <label className="row gap-2" style={{ alignItems: 'center', fontSize: 12.5, color: 'var(--fg-3)' }}>
              <span style={{ whiteSpace: 'nowrap' }}>音色</span>
              <select className="input" value={voice} disabled={ready || connecting} onChange={(e) => setVoice(e.target.value)} style={{ height: 32, maxWidth: 180 }}>
                {VOICES.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
              </select>
            </label>
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--fg-4)' }}>双向流式：连接后可反复输入文本合成（边输入边播放）；结束会话才断开。文本以句号/问号收尾合成更快（豆包按句子边界触发）。</div>
        </div>
      </div>

      {errorMsg && (
        <div className="row gap-2" style={{ padding: '10px 12px', background: 'rgba(231,76,60,0.08)', border: '1px solid rgba(231,76,60,0.25)', borderRadius: 8, fontSize: 12.5, color: 'var(--err)' }}>
          <XIcons.IconInfo size={13} /> <span style={{ overflowWrap: 'anywhere' }}>{errorMsg}</span>
        </div>
      )}

      {/* 输出可视化 */}
      <div className="card">
        <div className="row between" style={{ padding: '10px 14px', borderBottom: '1px solid var(--line)' }}>
          <div className="micro">输出</div>
          {caption && <XPrim.CopyBtn text={caption} />}
        </div>
        <div className="col" style={{ alignItems: 'center', justifyContent: 'center', gap: 14, padding: '26px 16px', minHeight: 240,
          background: 'radial-gradient(120% 80% at 50% 32%, rgba(80,120,220,0.06), transparent)' }}>
          <WsRealtimeOrb micAnalyserRef={micAnalyserRef} outAnalyserRef={outAnalyserRef} phase={ready || connecting ? phase : 'idle'} size={160} />
          <div className="micro" style={{ color: 'var(--fg-3)' }}>{PHASE_LABEL[ready ? phase : connecting ? 'connecting' : 'idle']}</div>
          <div style={{ fontSize: 14, color: 'var(--fg-1)', textAlign: 'center', maxWidth: 520, overflowWrap: 'anywhere', minHeight: 20 }}>
            {caption || <span style={{ color: 'var(--fg-4)' }}>合成的文本会累积显示在这里</span>}
          </div>
        </div>
        {/* 输入区（会话就绪才可发） */}
        <div className="row gap-2" style={{ padding: 12, borderTop: '1px solid var(--line)', alignItems: 'flex-end' }}>
          <textarea className="input" rows={2} style={{ resize: 'none', flex: 1 }}
            placeholder={ready ? '输入文本，回车或点发送合成…' : '先连接会话…'} value={text} disabled={!ready}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendText(); } }} />
          <div className="col gap-1" style={{ alignItems: 'flex-end' }}>
            <button className="btn btn-primary" style={{ minWidth: 90 }} disabled={!ready || !text.trim()} onClick={sendText}>发送合成</button>
            <span style={{ fontSize: 11, color: 'var(--fg-4)', whiteSpace: 'nowrap' }}>
              本段 {[...text].length} 字{sentChars > 0 ? ` · 会话累计 ${sentChars} 字${sessionCost > 0 ? ` ~$${sessionCost.toFixed(6)}` : ''}` : ''}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
